var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @module cercaliagl/control/LogisticRestrictions
 */
import Control from './Control';
import MapControls from './MapControls';
import { i18n } from '../dictionary';
import Servers from '../servers';
import VControlButton from '../view/VControlButton';
import WMS from '../layer/WMS';
import VLogisticRestrictions, { EventType as LogisticRestrictionsEventType } from '../view/VLogisticRestrictions';
/**
 * @typedef {Object} LogisticsWMSParams
 * @property {number} [lweight] Max. weight
 * @property {number} [lweightaxl] Max. axle weight
 * @property {number} [lheight] Max. height
 * @property {number} [llength] Max. length
 * @property {number} [lwidth] Max. width
 * @property {boolean} [rimp] Dangerous goods
 */
/**
 * @classdesc
 * <b>Javascript ES5</b>
 *
 * ```js
 * const control = new cercaliagl.control.LogisticRestrictions();
 * ```
 * <br/>
 * @extends {Control}
 * @api
 */
var LogisticRestrictions = /** @class */ (function (_super) {
    __extends(LogisticRestrictions, _super);
    /**
     * @constructor
     */
    function LogisticRestrictions() {
        var _this = this;
        var controlButton = new VControlButton('logistics');
        var element = controlButton.getElement();
        var logisticRestrictionsDialog = new VLogisticRestrictions();
        _this = _super.call(this, {
            element: element,
            className: 'cercalia.control.LogisticsRestrictions',
            name: MapControls.LOGISTICS_RESTRICTIONS,
            dialog: logisticRestrictionsDialog.getElement(),
        }) || this;
        /**
         * @private
         * @type {boolean}
         */
        _this.enabled_ = false;
        /**
         * @private
         * @type {Array.<string>}
         */
        _this.LOGISTICS_PARAMS_ = ['lweight', 'llength', 'lweightaxl', 'lwidth', 'lheight', 'rimp'];
        /**
         * @private
         * @type {VControlButton}
         */
        _this.controlButton_ = controlButton;
        /**
         * @private
         * @type {VLogisticRestrictions}
         */
        _this.logisticRestrictionsDialog_ = logisticRestrictionsDialog;
        /**
         * @private
         * @type {HTMLElement}
         */
        _this.element_ = element;
        /**
         * @private
         * @type {WMS|undefined}
         */
        _this.wmsLayer_ = undefined;
        _this.initialize_();
        return _this;
    }
    /**
     * @private
     */
    LogisticRestrictions.prototype.initialize_ = function () {
        this.controlButton_.setTooltip(i18n('tooltipLogisticsRestrictions'));
        this.element_.addEventListener('click', this.handleClick_.bind(this));
        this.logisticRestrictionsDialog_.on(LogisticRestrictionsEventType.CHANGE, this.createOrUpdateWMS_.bind(this));
    };
    /**
     * @private
     */
    LogisticRestrictions.prototype.handleClick_ = function () {
        if (!this.enabled_) {
            this.enable();
        }
        else {
            this.disable();
        }
    };
    /**
     * Enable Logistic Restrictions control. Show dialog
     * @override
     * @api
     */
    LogisticRestrictions.prototype.enable = function () {
        this.enabled_ = true;
        this.controlButton_.enable();
        this.logisticRestrictionsDialog_.reset();
        this.logisticRestrictionsDialog_.show();
    };
    /**
     * Disable logisticrestrictions control.
     * @override
     * @api
     */
    LogisticRestrictions.prototype.disable = function () {
        var cercaliaMap = this.getCercaliaMap();
        this.enabled_ = false;
        this.controlButton_.disable();
        this.logisticRestrictionsDialog_.hide(false);
        if (this.wmsLayer_ !== undefined) {
            cercaliaMap.removeLayer(this.wmsLayer_);
            this.wmsLayer_ = undefined;
        }
    };
    /**
     * Returns `cercalia` Map object
     * @return {import('../Map').default} Cercalia map.
     * @api
     */
    LogisticRestrictions.prototype.getCercaliaMap = function () {
        return this.map_.getCercaliaMap();
    };
    /**
     * @private
     * @param {LogisticsWMSParams} restrictionsParams Restriction parameters.
     * @return {string|null} Layers string parameter with enabled restrictions.
     */
    LogisticRestrictions.prototype.getRestrictionsLayers_ = function (restrictionsParams) {
        var _this = this;
        if (!restrictionsParams) {
            return null;
        }
        var restrictionsKeysEnabled = [];
        var restrictionsKeys = Object.keys(restrictionsParams);
        restrictionsKeys.forEach(function (restrictionKey) {
            var restrictionValue = restrictionsParams[restrictionKey];
            if (_this.LOGISTICS_PARAMS_.indexOf(restrictionKey) !== -1 && restrictionValue) {
                restrictionsKeysEnabled.push(restrictionKey);
            }
        });
        return restrictionsKeysEnabled.join(',');
    };
    /**
     * @private
     * @param {Object.<string, string>} restrictionsParams Restrictions params.
     */
    LogisticRestrictions.prototype.createOrUpdateWMS_ = function (restrictionsParams) {
        var cercaliaMap = this.getCercaliaMap();
        var paramLayers;
        var params;
        if (this.wmsLayer_ === undefined) {
            paramLayers = this.getRestrictionsLayers_(restrictionsParams);
            if (paramLayers.length > 0) {
                this.wmsLayer_ = new WMS({
                    layerId: 'CercaliaLogisticsRestrictions',
                    url: Servers.logistics,
                    params: __assign({ service: 'WMS', version: '1.1.1', request: 'GetMap', format: 'image/png', styles: '', width: 1024, height: 1024, layers: paramLayers }, restrictionsParams),
                });
                cercaliaMap.addLayer(this.wmsLayer_);
            }
        }
        else {
            params = this.wmsLayer_.getParams();
            params = this.updateParams_(params, restrictionsParams);
            if (params) {
                paramLayers = this.getRestrictionsLayers_(restrictionsParams);
                if (paramLayers) {
                    params.layers = paramLayers;
                    this.wmsLayer_.updateParams(params);
                }
                else {
                    cercaliaMap.removeLayer(this.wmsLayer_);
                    this.wmsLayer_ = undefined;
                }
            }
            else {
                cercaliaMap.removeLayer(this.wmsLayer_);
                this.wmsLayer_ = undefined;
            }
        }
    };
    /**
     * @private
     * @param {Object.<string, string>} params Parameters.
     * @param {LogisticsWMSParams} restrictionsParams Restrictions params.
     * @return {Object.<string, string>} Parameters or `null` if all checkbox are not selected.
     */
    LogisticRestrictions.prototype.updateParams_ = function (params, restrictionsParams) {
        // eslint-disable-next-line no-param-reassign
        this.LOGISTICS_PARAMS_.forEach(function (param) { return delete params[param]; });
        var returnParams = __assign(__assign({}, params), restrictionsParams);
        return returnParams;
    };
    /**
     * Close dialog
     * @api
     */
    LogisticRestrictions.prototype.closePopup = function () {
        this.logisticRestrictionsDialog_.hide();
    };
    return LogisticRestrictions;
}(Control));
export default LogisticRestrictions;
//# sourceMappingURL=LogisticRestrictions.js.map